/**
 * Invoice entity types — list items, detail, images, GRN mappings,
 * and request/response shapes for list queries.
 *
 * Aligned with: v2-invoice-api-contract.md (commit 0607bbe)
 */
import type { Action } from "./action";
import type { LifecycleStatus } from "./common";
import type { ApprovalLog, MatchException, RevisionRequest, ValidationCheck, ValidationResult } from "./action";
import type { PoSource } from "../constants/lifecycleStatus";

// --- Supporting entities ---

export type InvoiceImage = {
  file_name: string;
  pre_signed_url: string;
};

export type InvoiceGrnMapping = {
  id: number;
  invoice_id: number;
  grn_id: number;
  po_code: string;
  allocated_value: number;
  created_at: string;
  created_by: string;
};

// --- Credit note (embedded on invoice list item once BE adds include=credit_notes) ---

export type CreditNote = {
  id: number;
  number: string;
  status: "DRAFT" | "APPROVED" | "REJECTED";
  credit_note_value?: number | null;
  amount?: number | null;
  credit_note_number?: string | null;
  reason?: string | null;
  invoice_ids?: number[];
  attachment_id?: string | null;
  utr_number?: string | null;
};

// --- Invoice list item (from GET /v2/vip/invoice) ---

export type V2InvoiceListItem = {
  id: number;
  invoice_no: string;
  po_code: string;
  vendor_id: number;
  vendor_name: string;
  invoice_date: string;
  invoice_value: number;
  lifecycle_status: LifecycleStatus;
  /** Old v1 invoices may only have `status` (no lifecycle_status). BE maps them, but field may be present. */
  status?: string;
  lifecycle_version: number;
  system_validation_status: number;
  has_open_exceptions: boolean;
  open_exception_types: string[];
  invoice_image?: InvoiceImage;
  created_at: string;
  modified: string;
  grn_amount?: number | null;
  /** PO originating system ("FINS"/"IMS"/"ZOHO"); drives source-specific labels, e.g. FINS UNDER_REVIEW → "OPS Approval Pending". */
  po_source?: PoSource;
  /** Present on FINS-source POs; may be absent or null on IMS/ZOHO POs. */
  business_unit?: string | null;
  /** Project city; rendered for all users. */
  project_city?: string | null;
  invoice_status?: { label: string; state: string };
  /** Populated once BE adds credit_notes to the list response */
  credit_notes?: CreditNote[];
};

// --- Match result (from GET /v2/invoice/:id?include=match_result) ---

export const MATCH_STATUS = {
  SUCCESS: "SUCCESS",
  QTY_VARIANCE: "QTY_VARIANCE",
  AMOUNT_MISMATCH: "AMOUNT_MISMATCH",
  DATE_MISMATCH: "DATE_MISMATCH",
  FAILED: "FAILED",
} as const;

export type MatchStatus = (typeof MATCH_STATUS)[keyof typeof MATCH_STATUS];

export type MatchResult = {
  id: number;
  invoice_id: number;
  po_code: string | null;
  grn_item_id: number | null;
  match_status: MatchStatus;
  qty_variance: number | null;
  amount_variance: number | null;
  variance_percent: number | null;
  match_score: number | null;
  notes: string | null;
  created_at: string;
};

// --- Invoice detail (from GET /v2/vip/invoice/:id) ---

/** The `invoice` object nested inside the detail response. */
export type V2Invoice = {
  id: number;
  invoice_no: string;
  po_code: string;
  invoice_value: number;
  invoice_date: string;
  lifecycle_status: LifecycleStatus;
  /** Old v1 invoices may only have `status` (no lifecycle_status). BE maps them, but field may be present. */
  status?: string;
  lifecycle_version: number;
  vendor_id: number;
  original_invoice_id: number | null;
  invoice_doc_url: string | null;
  billing_entity?: string | null;
  bill_to_address?: string | null;
  ship_to_address?: string | null;
  hsn_sac_codes?: string | string[] | null;
  grn_id?: number | null;
  grn_date?: string | null;
  project_id?: string | null;
  order_id?: string | null;
  order_type?: string | null;
  grn_allocation_status?: string | null;
  system_validation_status?: string | null;
  system_rejection_reason?: string | null;
  delete_remarks?: string | null;
  additional_charges?: number | null;
  irn_invoice?: boolean | null;
  parsed_data?: Record<string, unknown>;
  validation_result?: ValidationResult;
  created: string;
  modified: string;
};

/** GRN entry in the PATCH payload. */
export type GrnMappingInput = {
  grn_id: number;
  allocated_value: number;
};

/** Payload for PATCH /v2/invoice/:id — only changed fields + lifecycle_version + comments. */
export type UpdateInvoicePayload = {
  lifecycle_version: number;
  comments: string;
  invoice_number?: string;
  invoice_date?: string;
  invoice_value?: number;
  billing_entity?: string;
  bill_to_address?: string;
  ship_to_address?: string;
  hsn_sac_codes?: string[];
  additional_charges?: number;
  grn_mappings?: GrnMappingInput[];
};

/** Severity values returned in extraction_issues[].severity. */
export const EXTRACTION_ISSUE_SEVERITY = {
  MISSING_REQUIRED: "missing_required",
  AMBIGUOUS: "ambiguous",
  INCONSISTENT: "inconsistent",
} as const;

export type ExtractionIssueSeverity =
  (typeof EXTRACTION_ISSUE_SEVERITY)[keyof typeof EXTRACTION_ISSUE_SEVERITY];

/** A single field-level extraction issue from the OCR/parser agent. */
export type ExtractionIssue = {
  field: string;
  reason: string;
  severity: ExtractionIssueSeverity;
};

/** Line item extracted by the parser (inside ExtractedInvoiceData). */
export type ExtractedLineItem = {
  description: string | null;
  /** Digits as printed (parser rev 18+, ENG-27649); older parse rows still hold a number. */
  hsn_code: string | number | null;
  quantity: number | null;
  unit: string | null;
  unit_price: number | null;
  amount: number | null;
};

/** Agent's manual-vs-digital classification of a seal or signature. */
export type AuthenticationMarkType = "manual" | "digital";

/**
 * Authentication verdict for the whole document: seal / signature (agent
 * config_version 12+, VIP ADR 0038) plus the GST e-invoice credentials
 * (config_version 15+, VIP ADR 0045). `*_type` is null when the mark is absent
 * or its origin could not be determined. Every key is optional on the wire: a
 * partial block is a known agent contract slip, and a non-boolean `*_present`
 * means "no verdict" (render `—`, never a cross) — callers must not assume the
 * flag is set.
 */
export type DocumentAuthentication = {
  seal_present?: boolean | null;
  seal_type?: AuthenticationMarkType | null;
  signature_present?: boolean | null;
  signature_type?: AuthenticationMarkType | null;
  /** IRP e-invoice credentials; absent on parses older than config_version 15. */
  irn_present?: boolean | null;
  /** The IRN verbatim — a 64-char hex hash. Null when no IRN was found. */
  irn_number?: string | null;
  /** The e-invoice QR only; a UPI / payment QR never sets this. */
  qr_code_present?: boolean | null;
};

/** Decoded shape of latest_parse_response.response — the ExtractedInvoice payload from Answerly. */
export type ExtractedInvoiceData = {
  invoice_number: string | null;
  invoice_date: string | null;
  po_number: string | null;
  invoice_total_value: number | null;
  bill_to_entity: string | null;
  bill_to_address: string | null;
  bill_to_gstin: string | null;
  ship_to_entity: string | null;
  ship_to_address: string | null;
  ship_to_gstin: string | null;
  seller_entity: string | null;
  seller_gstin: string | null;
  /** Absent on parses before the seller-address parser instruction (ENG-27544). */
  seller_address?: string | null;
  line_items?: ExtractedLineItem[];
  extraction_issues?: ExtractionIssue[];
  /** Absent on parses older than agent config_version 12. */
  document_authentication?: DocumentAuthentication | null;
  error?: string | null;
};

/** Status values returned in latest_parse_response.status. */
export const PARSE_RESPONSE_STATUS = {
  SUCCESS: "SUCCESS",
  PARTIAL: "PARTIAL",
  FAILED: "FAILED",
} as const;

export type ParseResponseStatus =
  (typeof PARSE_RESPONSE_STATUS)[keyof typeof PARSE_RESPONSE_STATUS];

/** Latest invoice_agent_responses row for step=PARSE (returned when include=parse_result). */
export type ParseResultDetail = {
  id: number;
  invoice_id: number | null;
  step: "PARSE";
  status: ParseResponseStatus;
  execution_id?: string | null;
  response: ExtractedInvoiceData | null;
  metadata?: Record<string, unknown> | null;
  error?: string | null;
  created_at: string;
};

/** Validation step result (returned when include=validation_result). */
export type ValidationStepResult = {
  id: number;
  invoice_id: number;
  step: string;
  status: string;
  execution_id: string;
  response: {
    overall_status?: string;
    checks?: ValidationCheck[];
    mismatched_fields?: string[];
    [key: string]: unknown;
  } | null;
  metadata: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
};

/** Line item (returned when include=line_items). */
export type LineItem = {
  id: number;
  invoice_id: number;
  position: number;
  description: string;
  hsn_code: string | null;
  quantity: number;
  unit: string | null;
  unit_price: number;
  amount: number;
  created_at: string;
};

/** Full detail response — mirrors the API response shape. */
export type V2InvoiceDetailResponse = {
  invoice: V2Invoice;
  vendor_name: string;
  invoice_image: InvoiceImage;
  grn_mappings?: InvoiceGrnMapping[];
  grn_amount?: number | null;
  exceptions?: MatchException[];
  approval_logs?: ApprovalLog[];
  revision_requests?: RevisionRequest[];
  parse_result?: ParseResultDetail | null;
  line_items?: LineItem[];
  validation_result?: ValidationStepResult | null;
  /** PO originating system ("FINS"/"IMS"/"ZOHO"); drives source-specific labels, e.g. FINS UNDER_REVIEW → "OPS Approval Pending". */
  po_source?: PoSource;
  invoice_status?: { label: string; state: string } | null;
  payment_summary?: { payment_type?: string | null; requested_amount?: number | null; expected_payment_date?: string | null } | null;
  allowed_actions: Action[];
  match_result?: MatchResult | null;
  credit_notes?: CreditNote[];
};

// --- GRN mapping response (from GET /v2/vip/invoice/:id/grn-mapping) ---

export type GrnMappingResponse = {
  invoice_id: number;
  grn_allocations: InvoiceGrnMapping[];
};

// --- GRN availability response (from GET /v1/vip/grn-list?po_code=) ---

export type GrnItem = {
  grn_id: number;
  grn_value: number;
  grn_date: string | null;
};

export type GrnAvailabilityResponse = GrnItem[];

// --- List query params ---

export type InvoiceListParams = {
  limit?: number;
  offset?: number;
  lifecycle_status?: string | string[];
  status?: string;
  po_code?: string;
  invoice_number?: string;
  vendor_id?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  factory_code?: string;
  // v1 filter popup params
  invoice_status?: string;
  start_date?: string;
  end_date?: string;
  validation_status?: string;
  payment_status?: string;
};

// --- List response ---

export type InvoiceListResponse = {
  invoices: V2InvoiceListItem[];
  /**
   * Being removed by the BE (ENG-26609): it is a second full-table scan on every list
   * call. Optional so the FE can fall back to the /summary total once it is gone.
   */
  total_count?: number;
  status_counts: Record<string, number>;
  limit: number;
  offset: number;
};

// --- Summary params ---

/**
 * Filters accepted by GET /v2/vip/invoice/summary. The list tab passes the same
 * non-status filters it sends to the list endpoint so the sidebar counts and the
 * pagination total describe the same result set.
 */
export type InvoiceSummaryParams = {
  vendor_id?: number;
  po_code?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  factory_code?: string;
  // v1 filter popup params (mirrors InvoiceListParams)
  business_unit?: string;
  project_city?: string;
  start_date?: string;
  end_date?: string;
  payment_status?: string;
};

// --- Summary response ---

export type InvoiceSummaryResponse = {
  status_counts: Record<string, number>;
  total_count: number;
};

// --- Upload request ---

export type UploadInvoiceFields = {
  vendor_id: number;
  invoice_number: string;
  po_number: string;
  invoice_date: string;
  invoice_amount: number;
  file: File;
  original_invoice_id?: number;
};

// --- Upload response ---

export type UploadInvoiceResponse = {
  invoice_id: number;
  status: string;
  message: string;
};

// --- Status timeline (from GET /v2/vip/invoice/:id/status) ---

export type TimelineEvent = {
  timestamp: string;
  event_type: string;
  actor: string;
  details: string;
};

export type InvoiceStatusResponse = {
  invoice_id: number;
  invoice_no: string;
  lifecycle_status: LifecycleStatus;
  lifecycle_version: number;
  timeline: TimelineEvent[];
  revision_requests: RevisionRequest[];
  original_invoice_id: number | null;
};
